import type { TicketChatMessage } from "../Stores/Ticket";
import { isCaptureLogsNotice } from "./CaptureRequest";
import { isBotTeamReply } from "./MessageAuthor";

// A message authored in the dashboard has `user` populated; the customer's own
// messages (SHARED_COMMENT / USER_TEXT / BOT_REPLY) never do.
const isAgentAuthored = (comment?: TicketChatMessage) =>
  (comment?.type !== "BOT" && !!comment?.user) || isBotTeamReply(comment);

// Does a newly-created comment end the bot's turn — i.e. clear the "Thinking…"
// / streaming indicator?
//
// Any bot message ends it, and so does a human agent taking over: Kai is not the
// one speaking any more, and once a human answers, the bot's own final
// MESSAGE_CREATED — the only thing that used to clear the indicator — never
// arrives. Leaving it up strands "Thinking…" under the agent's reply forever AND
// keeps the composer's send button disabled, so the customer can no longer reply
// at all.
//
// The exception is an AI reply already staged for the typewriter: that one still
// belongs to the animation, which clears the indicator when it commits.
export const commentEndsBotTurn = (
  comment: TicketChatMessage | undefined,
  hasPendingAIReply: boolean,
) => {
  // The diagnostic-logs notice is a system line the server posts whenever
  // logs arrive — possibly mid-turn while an agent waits for them. It says
  // nothing about the bot being done.
  if (isCaptureLogsNotice(comment)) {
    return false;
  }

  const isBotMessage = comment?.type === "BOT";
  const isAIReply = isBotMessage && comment?.data?.answerBot === true;

  if (isAIReply) {
    return true;
  }

  return (isBotMessage || isAgentAuthored(comment)) && !hasPendingAIReply;
};

// Judge the bot's turn from a full comment snapshot instead of a live event.
//
// The MESSAGE_CREATED handler drops events while the widget is closed
// (notification bubbles take over), so a conversation reopened mid- or
// post-turn may have missed the very comment whose application clears the
// streaming/"Thinking…" bubble and resets canSetBotWrittingText. BOT_WRITTING
// carries no closed-widget gate, so the stale bubble even keeps updating while
// nobody is watching. After the reopen refetch, the snapshot is the only truth
// left — nothing further is coming on the wire for a turn that already ended.
//
// Returns true when the last visible comment ends the bot's turn (indicator
// state is stale — clear it), false when the customer's message is still the
// tail (the turn is legitimately running — let the indicator render again),
// and undefined when there is nothing visible to judge.
//
// Hidden workflow comments are skipped for the same reason as in
// getAwaitingReplyTailId: they never render, so they must not count as "the
// bot responded".
export const snapshotEndsBotTurn = (
  comments: TicketChatMessage[] | null | undefined,
  opts?: { voiceCallBusy?: boolean },
): boolean | undefined => {
  if (!comments || comments.length === 0) {
    return undefined;
  }

  for (let i = comments.length - 1; i >= 0; i--) {
    const comment = comments[i];
    if (comment?.data?.hiddenAction || isCaptureLogsNotice(comment)) {
      continue;
    }
    // A spoken customer turn whose answer never landed (call ended mid-turn —
    // an interrupted answer that produced nothing is deliberately not
    // persisted): once the call is over, nothing more is coming for it, so it
    // must not read as "the turn is legitimately running" — that stranded
    // "Thinking…" under it forever. While the call is live, it counts
    // normally (the answer really is on its way).
    if (comment?.type === "BOT_REPLY" && comment?.data?.voice && !opts?.voiceCallBusy) {
      return true;
    }
    return commentEndsBotTurn(comment, false);
  }

  return undefined;
};
